import type { PageNumber, PDFSplitResult } from "@pdfking/renderer-api"
import type { TaskContext, TrackingCallback } from "@pdfking/taskmaker"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { Min } from "@pdfking/math"
import { PDFConvert, PDFSplit } from "@pdfking/renderer-default"
import { Peek, Range } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ConvertPixels } from "../geometry/ConvertPixels"
import { FileContentType } from "../methods/FileContentType"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

type SplitPagesResult = {
  page_count: PageNumber
} & PDFSplitResult

const SplitPages = async function* (
  engine: TaskContext<EngineTasks>["engine"],
  options: PdfEngineOptions,
  file: VirtualFile,
  start_page: number,
  end_page: number,
  emit: TrackingCallback
): AsyncGenerator<SplitPagesResult> {
  const { size, stream } = await RequestBlob(engine, options, file, emit)
  const type = FileContentType(file)

  switch (type) {
    case ContentType.PDF: {
      let page_count: PageNumber = 0 as PageNumber
      for await (const result of PDFSplit({ size, stream, type }, total => {
        page_count = total as PageNumber
        return Range(start_page, Min(end_page, total))
      })) {
        yield { ...result, page_count }
      }
      break
    }

    case ContentType.PNG:
    case ContentType.JPEG: {
      const result = await PDFConvert({ size, stream, type }, options.image_dpi)
      yield { ...result, page_count: 1 as PageNumber }
      break
    }
  }
}

export const TaskSplit = async function* ({
  emit,
  engine,
  options,
  source,
  start_page,
  end_page,
}: TaskContext<
  EngineTasks,
  {
    readonly options: PdfEngineOptions
    readonly source: VirtualFile
    readonly start_page: PageNumber
    readonly end_page: PageNumber
  }
>): AsyncGenerator<SinglePagePdf> {
  const {
    stream,
    value: first,
    error,
  } = await Peek(
    SplitPages(engine, options, source, start_page, end_page, emit)
  )
  if (error) {
    throw error
  }

  if (!first) return

  const total_pages = first.page_count
  const actual_end = Min(end_page, total_pages)
  const total = actual_end - start_page + 1

  void emit({
    done: 0,
    name: TrackedTaskName.SPLIT_PDF,
    total,
  })

  let page_number: PageNumber = (start_page - 1) as PageNumber

  for await (const { blob, media_box, page_count, trim_box, size } of stream) {
    page_number = (page_number + 1) as PageNumber

    const content = await GenerateFile(
      engine,
      options,
      [blob],
      ContentType.PDF,
      emit
    )

    yield {
      content,
      context: undefined,
      enum: VirtualFileType.SINGLE_PAGE_PDF,
      file_meta: {},
      media_box: ConvertPixels(options, media_box),
      page: undefined,
      page_count,
      page_number,
      size: ConvertPixels(options, size),
      source,
      trim_box: ConvertPixels(options, trim_box),
    } as SinglePagePdf

    void emit({
      done: page_number - start_page + 1,
      name: TrackedTaskName.SPLIT_PDF,
      total,
    })
  }
}
